/**
 * Brand marks for the tools this site has one for.
 *
 * Five of the twelve. They come from yoursmartstack.com, which carries the
 * same five, so a tool is the same object in the storefront, on its directory
 * card and on the companion-app step. The marks are from simple-icons (CC0);
 * the marks themselves are the trademarks of their owners and are used here
 * only to identify which tool is which.
 *
 * Inline SVG rather than <img src>, for two reasons. The site's
 * Content-Security-Policy sets img-src to 'self' data:, so a vendor-hosted
 * icon would be blocked outright — and the two assets this site already lost
 * to the Manus CDN shutdown are why nothing here hotlinks a logo anyway.
 *
 * The other seven tools have no mark and render nothing, so their card falls
 * back to the emoji it has always shown. A missing icon is a gap; a
 * placeholder box next to a product name is a defect.
 *
 * Gemini is the odd one: Google's four-colour gradient, so it sets its own
 * fill and ignores currentColor. Everything else inherits, which is what lets
 * one component serve both tones. The gradient carries a fixed id, which is
 * safe only while at most one Gemini mark renders per page — the directory
 * shows each tool once, and a path has at most one app step. Two on a page
 * would collide, so make the id unique per instance if that changes.
 */

import type { ReactNode } from "react";

/** Keyed by tool id, which is also the products.key for four of the five. */
const MARKS: Record<string, ReactNode> = {
  "opus-clip": (
    <g transform="scale(0.046875)">
      <path d="M511.31 255.75C511.62 256.04 511.69 256.35 511.41 256.7C511.27 256.48 511.14 256.26 511 256.05C511.1 255.95 511.21 255.86 511.31 255.76V255.75Z" /> <path d="M247.885 10.2239C251.683 9.85202 260.359 10.0214 264.068 10.1762C329.78 12.578 391.827 41.0577 436.494 89.3178C480.351 137.38 503.593 200.734 501.22 265.755C498.516 331.541 469.785 393.557 421.351 438.161C380.871 475.368 328.928 497.674 274.073 501.403C255.196 502.683 239.855 501.586 221.275 500.785C220.957 483.073 221.063 465.035 220.966 447.302C220.931 440.975 220.524 425.069 221.217 419.703L221.801 419.36C227.461 422.284 236.726 422.678 243.067 423.246C269.749 425.662 296.082 420.97 320.757 410.807C334.916 404.975 347.674 397.898 359.685 388.329C389.738 364.138 410.865 330.619 419.728 293.075C429.7 250.198 420.435 202.946 397.21 165.783C391.187 156.152 384.194 147.161 376.341 138.953C346.567 107.809 305.618 89.7955 262.539 88.8894C215.124 87.6823 175.366 102.438 140.909 135.207C119.086 155.997 103.404 182.392 95.5804 211.499C89.1751 236.176 89.8762 254.634 89.8745 279.646L89.8867 326.487L89.9088 435.702C89.9096 457.542 90.2008 480.216 89.6422 501.971C87.7066 501.73 85.3146 501.717 83.3476 501.738C59.4966 501.988 35.4794 501.297 11.6492 502.103C12.1243 482.81 11.5831 461.009 11.5742 441.492L11.5547 316.328L11.5593 273.567C11.5767 249.676 11.5801 229.044 16.7207 205.404C25.8019 162.984 45.7864 123.664 74.7016 91.3251C120.821 40.1163 179.575 13.8274 247.885 10.2239Z" /> <path d="M250.056 115.758C258.176 115.147 267.427 115.96 275.5 117.032C352.466 127.256 406.367 198.767 395.132 276.04C389.491 313.913 368.948 347.96 338.068 370.606C331.8 375.277 326.939 377.839 320.136 381.439C288.527 398.166 255.844 398.032 221.324 392.964C220.648 367.021 221.288 340.887 220.931 314.927C220.89 311.948 220.937 309.283 220.559 306.301C226.452 312.228 238.907 316.552 247.101 317.948C262.565 320.583 280.279 315.611 292.925 306.517C306.26 297.02 315.203 282.547 317.73 266.373C320.251 250.683 316.061 231.996 306.719 219.121C297.173 205.825 282.66 196.949 266.476 194.51C246.229 191.664 227.175 197.396 212.675 211.905C203.886 220.655 197.893 231.819 195.459 243.98C193.655 253.419 194.182 267.807 194.209 277.81L194.224 323.177L194.273 502.116C169.381 501.836 141.034 501.734 116.262 502.527L116.298 326.028L116.293 280.924C116.277 262.17 115.574 242.333 119.949 224C134.798 161.774 186.659 119.407 250.056 115.758Z" />
    </g>
  ),
  "claude": (
    <>
      <path d="m4.7144 15.9555 4.7174-2.6471.079-.2307-.079-.1275h-.2307l-.7893-.0486-2.6956-.0729-2.3375-.0971-2.2646-.1214-.5707-.1215-.5343-.7042.0546-.3522.4797-.3218.686.0608 1.5179.1032 2.2767.1578 1.6514.0972 2.4468.255h.3886l.0546-.1579-.1336-.0971-.1032-.0972L6.973 9.8356l-2.55-1.6879-1.3356-.9714-.7225-.4918-.3643-.4614-.1578-1.0078.6557-.7225.8803.0607.2246.0607.8925.686 1.9064 1.4754 2.4893 1.8336.3643.3035.1457-.1032.0182-.0728-.164-.2733-1.3539-2.4467-1.445-2.4893-.6435-1.032-.17-.6194c-.0607-.255-.1032-.4674-.1032-.7285L6.287.1335 6.6997 0l.9957.1336.419.3642.6192 1.4147 1.0018 2.2282 1.5543 3.0296.4553.8985.2429.8318.091.255h.1579v-.1457l.1275-1.706.2368-2.0947.2307-2.6957.0789-.7589.3764-.9107.7468-.4918.5828.2793.4797.686-.0668.4433-.2853 1.8517-.5586 2.9021-.3643 1.9429h.2125l.2429-.2429.9835-1.3053 1.6514-2.0643.7286-.8196.85-.9046.5464-.4311h1.0321l.759 1.1293-.34 1.1657-1.0625 1.3478-.8804 1.1414-1.2628 1.7-.7893 1.36.0729.1093.1882-.0183 2.8535-.607 1.5421-.2794 1.8396-.3157.8318.3886.091.3946-.3278.8075-1.967.4857-2.3072.4614-3.4364.8136-.0425.0304.0486.0607 1.5482.1457.6618.0364h1.621l3.0175.2247.7892.522.4736.6376-.079.4857-1.2142.6193-1.6393-.3886-3.825-.9107-1.3113-.3279h-.1822v.1093l1.0929 1.0686 2.0035 1.8092 2.5075 2.3314.1275.5768-.3218.4554-.34-.0486-2.2039-1.6575-.85-.7468-1.9246-1.621h-.1275v.17l.4432.6496 2.3436 3.5214.1214 1.0807-.17.3521-.6071.2125-.6679-.1214-1.3721-1.9246L14.38 17.959l-1.1414-1.9428-.1397.079-.674 7.2552-.3156.3703-.7286.2793-.6071-.4614-.3218-.7468.3218-1.4753.3886-1.9246.3157-1.53.2853-1.9004.17-.6314-.0121-.0425-.1397.0182-1.4328 1.9672-2.1796 2.9446-1.7243 1.8456-.4128.164-.7164-.3704.0667-.6618.4008-.5889 2.386-3.0357 1.4389-1.882.929-1.0868-.0062-.1579h-.0546l-6.3385 4.1164-1.1293.1457-.4857-.4554.0608-.7467.2307-.2429 1.9064-1.3114Z" />
    </>
  ),
  "chatgpt": (
    <>
      <path d="M22.2819 9.8211a5.9847 5.9847 0 0 0-.5157-4.9108 6.0462 6.0462 0 0 0-6.5098-2.9A6.0651 6.0651 0 0 0 4.9807 4.1818a5.9847 5.9847 0 0 0-3.9977 2.9 6.0462 6.0462 0 0 0 .7427 7.0966 5.98 5.98 0 0 0 .511 4.9107 6.051 6.051 0 0 0 6.5146 2.9001A5.9847 5.9847 0 0 0 13.2599 24a6.0557 6.0557 0 0 0 5.7718-4.2058 5.9894 5.9894 0 0 0 3.9977-2.9001 6.0557 6.0557 0 0 0-.7475-7.0729zm-9.022 12.6081a4.4755 4.4755 0 0 1-2.8764-1.0408l.1419-.0804 4.7783-2.7582a.7948.7948 0 0 0 .3927-.6813v-6.7369l2.02 1.1686a.071.071 0 0 1 .038.052v5.5826a4.504 4.504 0 0 1-4.4945 4.4944zm-9.6607-4.1254a4.4708 4.4708 0 0 1-.5346-3.0137l.142.0852 4.783 2.7582a.7712.7712 0 0 0 .7806 0l5.8428-3.3685v2.3324a.0804.0804 0 0 1-.0332.0615L9.74 19.9502a4.4992 4.4992 0 0 1-6.1408-1.6464zM2.3408 7.8956a4.485 4.485 0 0 1 2.3655-1.9728V11.6a.7664.7664 0 0 0 .3879.6765l5.8144 3.3543-2.0201 1.1685a.0757.0757 0 0 1-.071 0l-4.8303-2.7865A4.504 4.504 0 0 1 2.3408 7.872zm16.5963 3.8558L13.1038 8.364 15.1192 7.2a.0757.0757 0 0 1 .071 0l4.8303 2.7913a4.4944 4.4944 0 0 1-.6765 8.1042v-5.6772a.79.79 0 0 0-.407-.667zm2.0107-3.0231l-.142-.0852-4.7735-2.7818a.7759.7759 0 0 0-.7854 0L9.409 9.2297V6.8974a.0662.0662 0 0 1 .0284-.0615l4.8303-2.7866a4.4992 4.4992 0 0 1 6.6802 4.66zM8.3065 12.863l-2.02-1.1638a.0804.0804 0 0 1-.038-.0567V6.0742a4.4992 4.4992 0 0 1 7.3757-3.4537l-.142.0805L8.704 5.459a.7948.7948 0 0 0-.3927.6813zm1.0976-2.3654l2.602-1.4998 2.6069 1.4998v2.9994l-2.5974 1.4997-2.6067-1.4997Z" />
    </>
  ),
  "perplexity": (
    <>
      <path d="M22.3977 7.0896h-2.3106V.0676l-7.5094 6.3542V.1577h-1.1554v6.1966L4.4904 0v7.0896H1.6023v10.3976h2.8882V24l6.932-6.3591v6.2005h1.1554v-6.0469l6.9318 6.1807v-6.4879h2.8882V7.0896zm-3.4657-4.531v4.531h-5.355l5.355-4.531zm-13.2862.0676 4.8691 4.4634H5.6458V2.6262zM2.7576 16.332V8.245h7.8476l-6.1149 6.1147v1.9723H2.7576zm2.8882 5.0404v-3.8852h.0001v-2.6488l5.7763-5.7764v7.0111l-5.7764 5.2993zm12.7086.0248-5.7766-5.1509V9.0618l5.7766 5.7766v6.5588zm2.8882-5.0652h-1.733v-1.9723L13.3948 8.245h7.8478v8.087z" />
    </>
  ),
  "gemini": (
    <>
      <defs><linearGradient id="gemini-grad-a" x1="0" y1="24" x2="24" y2="0" gradientUnits="userSpaceOnUse"><stop offset="0" stopColor="#1ba463" /><stop offset=".35" stopColor="#4285f4" /><stop offset=".7" stopColor="#d96570" /><stop offset="1" stopColor="#f2c94c" /></linearGradient></defs><path fill="url(#gemini-grad-a)" d="M11.04 19.32Q12 21.51 12 24q0-2.49.93-4.68.96-2.19 2.58-3.81t3.81-2.55Q21.51 12 24 12q-2.49 0-4.68-.93a12.3 12.3 0 0 1-3.81-2.58 12.3 12.3 0 0 1-2.58-3.81Q12 2.49 12 0q0 2.49-.96 4.68-.93 2.19-2.55 3.81a12.3 12.3 0 0 1-3.81 2.58Q2.49 12 0 12q2.49 0 4.68.96 2.19.93 3.81 2.55t2.55 3.81" />
    </>
  ),
  "gemini-notebook": (
    <>
      <path d="M11.999 3.201C5.372 3.201 0 8.528 0 15.101V20.8h2.212v-.568c0-2.666 2.178-4.827 4.866-4.827 2.688 0 4.866 2.16 4.866 4.827v.568h2.212v-.568c0-3.877-3.17-7.019-7.078-7.019A7.075 7.075 0 0 0 2.992 14.5a7.355 7.355 0 0 1 6.568-4.016c4.057 0 7.347 3.264 7.347 7.287V20.8h2.212V17.77c0-5.235-4.28-9.481-9.56-9.481a9.563 9.563 0 0 0-6.217 2.28A9.795 9.795 0 0 1 12 5.393c5.406 0 9.788 4.346 9.788 9.707V20.8H24V15.1c-.001-6.573-5.373-11.9-12.001-11.9Z" />
    </>
  ),
};

/**
 * Marks supplied as artwork rather than vector.
 *
 * Normalised on the way in — background knocked out, recoloured to the same
 * #2B2622 the inline marks render at, cropped to the artwork, squared on the
 * longer edge and written at 96px for a 28px tile at 3x. Gamma and Agent Opus
 * arrived as white artwork, which is invisible on a light tile; recolouring
 * rather than inverting is one rule that covers white art, black art and the
 * two that shipped their own background.
 *
 * Self-hosted under /assets/marks, like everything else here: img-src is
 * 'self' data:, and the two assets this site lost to the Manus CDN shutdown
 * are why nothing hotlinks a vendor logo.
 *
 * Replacing any of these with real vector art means moving it into MARKS
 * above and deleting the entry here. That is the better end state — these are
 * bitmaps of somebody's trademark — but a bitmap that renders beats a card
 * with nothing on it.
 */
const RASTER_MARKS: Record<string, { src: string; ground?: "brand" }> = {
  gamma: { src: "/assets/marks/gamma.png" },
  manus: { src: "/assets/marks/manus.png" },
  castmagic: { src: "/assets/marks/castmagic.png" },
  "agent-opus": { src: "/assets/marks/agent-opus.png" },
  littlebird: { src: "/assets/marks/littlebird.png" },

  /* The Resource Center's three, kept in their own colours.
   *
   * `ground: "brand"` puts them on the site's navy instead of the muted tile.
   * Not decoration — a legibility fix. HighLevel's arrows are yellow and
   * Open.Video's mark is cyan, and both sit at about 1.5:1 against a light
   * grey, which is invisible. All three are bright, saturated marks drawn to
   * be seen on something darker than themselves, and Canva's teal-to-purple
   * gradient reads better there too. Recolouring them to match the monochrome
   * marks was the other option and it would have thrown away the only thing
   * that makes them recognisable. */
  gohighlevel: { src: "/assets/marks/gohighlevel.png", ground: "brand" },
  canva: { src: "/assets/marks/canva.png", ground: "brand" },
  "open-video": { src: "/assets/marks/open-video.png", ground: "brand" },

  /* The two app builders keep their colours on the ORDINARY light tile, which
     is the one thing that separates them from the three above.

     They shipped on the brand ground on 3 Sep, by analogy with those three,
     and the analogy was wrong. On a wall of cards where every other tile is
     muted grey, two navy tiles read as a different kind of object rather than
     as two more tools — Caren spotted it on the live grid, which is the only
     place the comparison is actually visible.

     The legibility argument that earns those three their dark ground does not
     apply here. Measured worst-case against the muted tile: Base44's orange is
     2.15:1 and Lovable's lightest gradient stop 1.64:1, against HighLevel at
     1.12:1 and Canva at 1.00:1. The difference is not only the ratio — these
     two are solid filled shapes in mid-tone orange, so the silhouette carries
     them, where HighLevel's thin yellow arrows and Canva's pale gradient have
     nothing left once the contrast goes. Below 3:1 either way, which is why
     this is a judgement and not a rule, and why the numbers are written down:
     it is a decorative mark with the product name in text beside it, so no
     information is lost if a viewer only sees the shape.

     Still in colour, not flattened to #2B2622 like Gamma and Castmagic. The
     ask was a light background, and monochrome would throw away the gradient
     that makes Lovable's mark recognisable. */
  base44: { src: "/assets/marks/base44.png" },
  lovable: { src: "/assets/marks/lovable.png" },
};

/**
 * `products.key` -> brand id.
 *
 * None of them match. Four apps are sold as `<tool>-companion-app` and Claude
 * as 'quickwins' — the latter is in PRODUCTS.QUICKWINS, in every purchases row
 * already written and in the Quick Wins app's own entitlement check, so it
 * cannot be renamed to match the tool (migration 0008). The others are just
 * how they were entered. Either way the database is the authority and this map
 * is the seam.
 */
const BY_PRODUCT_KEY: Record<string, string> = {
  quickwins: "claude",
  "chatgpt-companion-app": "chatgpt",
  "perplexity-companion-app": "perplexity",
  "gemini-companion-app": "gemini",
  "gemini-notebook-companion-app": "gemini-notebook",
};

export function brandIdForProduct(productKey: string): string {
  return BY_PRODUCT_KEY[productKey] ?? productKey;
}

export function hasBrandMark(brandId: string): boolean {
  return brandId in MARKS || brandId in RASTER_MARKS;
}

/**
 * Two grounds, because the mark appears on both.
 *
 *   onNavy  the companion-app step: a cream tile so a dark mark reads, and so
 *           Google's gradient is not fighting a navy background.
 *   onCard  a directory card: the same tile in the site's muted grey, mark in
 *           the foreground colour.
 */
const TONES = {
  onNavy: "bg-primary-foreground text-[#2B2622]",
  onCard: "bg-muted text-foreground",
} as const;

/** The tile for a full-colour mark, which needs a dark ground to read. */
const BRAND_GROUND = "bg-primary";

export default function BrandMark({
  brandId,
  tone = "onCard",
  size = 36,
  className = "",
}: {
  brandId: string;
  tone?: keyof typeof TONES;
  /** Tile edge in px. The mark is drawn at roughly half of it. */
  size?: number;
  className?: string;
}) {
  const mark = MARKS[brandId];
  const raster = RASTER_MARKS[brandId];
  if (!mark && !raster) return null;

  const inner = Math.round(size * 0.53);
  const tile = raster?.ground === "brand" ? BRAND_GROUND : TONES[tone];

  return (
    <span
      aria-hidden="true"
      style={{ width: size, height: size, borderRadius: Math.round(size * 0.28) }}
      className={`shrink-0 grid place-items-center ${tile} ${className}`}
    >
      {mark ? (
        <svg
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
          focusable="false"
          style={{ width: inner, height: inner }}
          className="block"
          fill="currentColor"
        >
          {mark}
        </svg>
      ) : (
        /* alt="" and aria-hidden on the wrapper: the mark is decorative here,
           the product name is always beside it in text. */
        <img
          src={raster!.src}
          alt=""
          loading="lazy"
          style={{ width: inner, height: inner }}
          className="block object-contain"
        />
      )}
    </span>
  );
}
